import { createEventHook, type EventHook, type EventHookOff, type EventHookOn } from "@vueuse/core";
import { useFormLogger } from "./use-form-logger.js";
import type { GatingState } from "../types/definitions/gating/gating-state.js";
import {
  type InjectionValuesRecord,
  type InjectionKeysRecord,
  CreateInjectionKeysRecord,
  injectRequiredWithSelf,
  checkValuesAlreadyProvided
} from "@mcwd/util-vue";
import { provide } from "vue";

type EventHookFnParams<EventName extends string = string> = { eventName: EventName };

export type FormEventHooks = {
  marketoSetup: EventHook<{ eventName: "marketoSetup" }>;
  ready: EventHook<{ eventName: "ready" }>;
  userFirstInteraction: EventHook<{ eventName: "userFirstInteraction" }>,
  validationErrorOnSubmit: EventHook<{ eventName: "validationErrorOnSubmit" }>
  marketoValidation: EventHook<{ eventName: "marketoValidation"; marketoIsValid: boolean }>;
  submit: EventHook<{ eventName: "submit" }>;
  completed: EventHook<{
    eventName: "completed";
    gatingState: GatingState.MarketoForm;
  }>;
  modalClosed: EventHook<{
    eventName: "modalClosed";
  }>;
  formAbandonment: EventHook<{
    eventName: "formAbandonment"
  }>
};

export type FormEventNames = keyof FormEventHooks;

type InferEventHook<T> = T extends EventHook<infer TEventHookType> ? EventHook<TEventHookType> : never;

type InferEventHookParams<TFnType extends "on" | "off", T> =
  [InferEventHook<T>] extends [never]
    ? never
    : (
      TFnType extends "on"
        ? Parameters<InferEventHook<T>["on"]>[0]
        : 
      TFnType extends "off"
          ? Parameters<InferEventHook<T>["off"]>[0]
      : never
    );

type UseMarketoFormEventInjectionValues = InjectionValuesRecord<{
  useFormEventsValue: Readonly<FormEventHooks>;
  onEvent: <const EventName extends FormEventNames>(eventName: EventName, callback: InferEventHookParams<"on", FormEventHooks[EventName]> ) => ReturnType<EventHookOn>;
  offEvent: <const EventName extends FormEventNames>(eventName: EventName, callback: InferEventHookParams<"off", FormEventHooks[EventName]>) => ReturnType<EventHookOff>;
}>;

export type OnEventFn = UseMarketoFormEventInjectionValues["onEvent"];
export type OffEventFn = UseMarketoFormEventInjectionValues["offEvent"];

type UseMarketoFormEventInjectionKeys = InjectionKeysRecord<UseMarketoFormEventInjectionValues>;

const UseMarketoFormEventInjectionKeys = CreateInjectionKeysRecord<UseMarketoFormEventInjectionKeys>({
  useFormEventsValue: "useFormEventsValue",
  onEvent: "onEvent",
  offEvent: "offEvent"
});

export function initMarketoFormEvents() {
  if (checkValuesAlreadyProvided(UseMarketoFormEventInjectionKeys)) {
    return;
  }
  const marketoSetup = createEventHook<EventHookFnParams<"marketoSetup">>();
  const ready = createEventHook<EventHookFnParams<"ready">>();
  const userFirstInteraction = createEventHook<EventHookFnParams<"userFirstInteraction">>();
  const validationErrorOnSubmit = createEventHook<EventHookFnParams<"validationErrorOnSubmit">>();
  const marketoValidation = createEventHook<EventHookFnParams<"marketoValidation"> & { marketoIsValid: boolean }>();
  const submit = createEventHook<EventHookFnParams<"submit">>();
  const completed = createEventHook<EventHookFnParams<"completed"> & { gatingState: GatingState.MarketoForm }>();
  const modalClosed = createEventHook<EventHookFnParams<"modalClosed">>();
  const formAbandonment = createEventHook<EventHookFnParams<"formAbandonment">>();

  const formEventHooks: FormEventHooks = Object.freeze({
    marketoSetup,
    ready,
    userFirstInteraction,
    validationErrorOnSubmit,
    marketoValidation,
    submit,
    completed,
    modalClosed,
    formAbandonment
  });
  
  const { logger } = useFormLogger();

  const onEvent: UseMarketoFormEventInjectionValues[ "onEvent" ] = <const EventName extends FormEventNames>(
    eventName: EventName,
    callback: InferEventHookParams<"on", FormEventHooks[ EventName ]>
  ): ReturnType<EventHookOn> =>
  {
    logger.debug(`Calling onEvent("${eventName}", callback: ${callback.toString()}`);
    return formEventHooks[eventName].on(callback as any);
  };

  const offEvent: UseMarketoFormEventInjectionValues[ "offEvent" ] = <const EventName extends FormEventNames> (
    eventName: EventName,
    callback: InferEventHookParams<"off", FormEventHooks[ EventName ]>
  ) =>
  {
    logger.debug(`Calling offEvent("${eventName}", callback: ${callback.toString()}`);
    return formEventHooks[eventName].off(callback as any);
  };

  provide(UseMarketoFormEventInjectionKeys.useFormEventsValue, formEventHooks);
  provide(UseMarketoFormEventInjectionKeys.onEvent, onEvent);
  provide(UseMarketoFormEventInjectionKeys.offEvent, offEvent);
}

export function useFormEvents({ callerLabel }: { callerLabel?: string | undefined } = {}) {
  const { logger } = useFormLogger();
  try {
    callerLabel ??= "useFormEvents";
    const useFormEventsValue = injectRequiredWithSelf(UseMarketoFormEventInjectionKeys.useFormEventsValue);
    const onEvent = injectRequiredWithSelf(UseMarketoFormEventInjectionKeys.onEvent);
    const offEvent = injectRequiredWithSelf(UseMarketoFormEventInjectionKeys.offEvent);
    return Object.freeze({ ...useFormEventsValue, onEvent, offEvent });
  }
  catch (err) {
    logger.error(`InjectionError from ${callerLabel ?? 'unknown'}`);
    throw err;
  }
}